import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageShell } from '../../layout/Shell';
import { getReplay, listReplays } from '../../shared/api/client';
import { replayFromApi } from '../../shared/api/transform';
import { OverlaidChart, SyncContext } from '../../shared/design/Charts';
import { Icon } from '../../shared/design/Icons';
import { CompareMinimap } from '../../shared/design/Minimap';
import { EmptyState, TierBadge } from '../../shared/design/Primitives';
import { Scrubber } from '../../shared/design/Scrubber';
import { useAsync } from '../../shared/hooks/useAsync';
import { formatTime, Replay } from '../../shared/model/Replay';

const COLORS = ['#0099ff', '#ff6b35', '#b56bff', '#00ff88'];

export function ComparePage({ slotCount = 2 }: { slotCount?: number }) {
  const [search] = useSearchParams();
  const idsParam = search.get('ids');

  // If ?ids=a,b,c is provided, fetch those specifically. Otherwise take the
  // latest `slotCount` replays so the page is never empty when data exists.
  const state = useAsync<Replay[]>(async () => {
    if (idsParam) {
      const ids = idsParam.split(',').filter(Boolean).slice(0, 4);
      const results = await Promise.all(ids.map((id) => getReplay(id)));
      return results.map(replayFromApi);
    }
    const summaries = await listReplays(slotCount);
    if (summaries.length === 0) return [];
    const full = await Promise.all(summaries.slice(0, slotCount).map((s) => getReplay(s.ghostUid)));
    return full.map(replayFromApi);
  }, [idsParam, slotCount]);

  return (
    <PageShell active="compare">
      <div className="tm" style={{ maxWidth: 1500, margin: '0 auto', padding: '24px 24px 80px' }}>
        {state.loading && (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--fg-3)' }}>Loading replays…</div>
        )}
        {state.error && (
          <EmptyState
            title="Couldn't load replays"
            body={state.error.message}
            icon={<Icon.Warn />}
            accent="var(--bad)"
          />
        )}
        {!state.loading && !state.error && (state.data?.length ?? 0) === 0 && (
          <EmptyState
            title="Nothing to compare yet"
            body="Scan at least two replays before opening Compare."
            icon={<Icon.Compare />}
          />
        )}
        {!state.loading && !state.error && (state.data?.length ?? 0) > 0 && (
          <CompareView replays={state.data!} />
        )}
      </div>
    </PageShell>
  );
}

function CompareView({ replays }: { replays: Replay[] }) {
  const fastest = useMemo(() => replays.reduce((a, b) => (a.meta.raceTimeMS < b.meta.raceTimeMS ? a : b)), [replays]);
  const [scrubT, setScrubT] = useState(0.5);
  // Compare is only meaningful for replays of the same map.
  const mapSet = useMemo(() => new Set(replays.map((r) => r.meta.challengeUid)), [replays]);
  const sameMap = mapSet.size === 1;

  return (
    <SyncContext.Provider value={{ scrubT, hoverT: null, setHoverT: () => {} }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        {replays.map((r, i) => (
          <CompareSlot key={r.meta.ghostUid} replay={r} color={COLORS[i]} isFastest={r === fastest} />
        ))}
      </div>

      <div
        style={{
          background: sameMap ? 'var(--ok-12)' : 'var(--bad-12)',
          border: `1px solid ${sameMap ? 'var(--ok)' : 'var(--bad)'}`,
          borderRadius: 6,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <span style={{ color: sameMap ? 'var(--ok)' : 'var(--bad)' }}>
          {sameMap ? <Icon.Check /> : <Icon.Warn />}
        </span>
        <span style={{ fontSize: 12, color: sameMap ? 'var(--ok)' : 'var(--bad)', fontFamily: 'var(--font-mono)' }}>
          {sameMap
            ? <>All replays on same map: <strong>{replays[0].meta.map}</strong> · ChallengeUID matches</>
            : <>ChallengeUID mismatch — comparing replays from different maps is not meaningful</>}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-8)',
            padding: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div className="eyebrow">Overlaid racing lines</div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 10 }}>
              {replays.map((r, i) => (
                <label
                  key={r.meta.ghostUid}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    color: 'var(--fg-2)',
                    fontFamily: 'var(--font-mono)',
                    padding: '4px 8px',
                    background: 'var(--bg-surface-2)',
                    borderRadius: 4,
                    border: '1px solid var(--border)',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 1, background: COLORS[i] }} />
                  {r.meta.author}
                </label>
              ))}
            </div>
          </div>
          <div style={{ height: 440 }}>
            <CompareMinimap replays={replays} width={780} height={440} />
          </div>
        </div>

        <SectorTable replays={replays} fastest={fastest} colors={COLORS} />
      </div>

      <div style={{ marginTop: 16 }}>
        <DeltaTimeline replays={replays} fastest={fastest} colors={COLORS} />
      </div>

      <div style={{ marginTop: 14 }}>
        <Scrubber
          replay={fastest}
          scrubT={scrubT}
          onChange={setScrubT}
          playing={false}
          setPlaying={() => {}}
          speed={1}
          setSpeed={() => {}}
        />
      </div>

      <div style={{ marginTop: 18 }}>
        <h2 className="h2" style={{ marginBottom: 12 }}>Telemetry · overlaid</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <OverlaidChart replays={replays} colors={COLORS} dim="speed" label="Speed" unit="m/s" min={0} />
          <OverlaidChart replays={replays} colors={COLORS} dim="steer" label="Steering" unit="−1 ↔ +1" min={-1} max={1} />
          <OverlaidChart replays={replays} colors={COLORS} dim="gear" label="Gear" unit="1–5" min={0} max={5} step />
          <OverlaidChart replays={replays} colors={COLORS} dim="rpm" label="RPM" unit="rev/min" min={2000} max={8200} />
        </div>
      </div>

      <div style={{ height: 80 }} />
    </SyncContext.Provider>
  );
}

function CompareSlot({ replay, color, isFastest }: { replay: Replay; color: string; isFastest: boolean }) {
  const meta = replay.meta;
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: 'var(--bg-surface)',
        border: `1px solid ${isFastest ? 'var(--ok)' : 'var(--border)'}`,
        borderLeft: `3px solid ${color}`,
        borderRadius: 'var(--r-8)',
        padding: '12px 14px',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ width: 6, height: 6, borderRadius: 1, background: color }} />
        <span className="eyebrow">{meta.map}</span>
        {isFastest && (
          <span
            style={{
              fontSize: 9,
              padding: '1px 5px',
              background: 'var(--ok)',
              color: '#051019',
              borderRadius: 2,
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              letterSpacing: '0.05em',
            }}
          >
            FASTEST
          </span>
        )}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--fg-1)', marginBottom: 2 }}>{meta.author}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
        <span className="num" style={{ fontSize: 22, fontWeight: 600, color: 'var(--fg-1)' }}>
          {meta.raceTimeStr}
        </span>
        <TierBadge tier={meta.tier} size="sm" label={false} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>
          {meta.topSpeedKmh} km/h
        </span>
      </div>
    </div>
  );
}

function SectorTable({ replays, fastest, colors }: { replays: Replay[]; fastest: Replay; colors: string[] }) {
  // Per-checkpoint deltas. Replays may have differing CP counts (e.g. early
  // exit, missing CPs) so use the smallest length to avoid off-by-one.
  const minCps = Math.min(...replays.map((r) => r.data.checkpoints.length));
  const sectorTimes = replays.map((r) =>
    r.data.checkpoints.slice(0, minCps).map((cp, i) => {
      const prev = i === 0 ? 0 : r.data.checkpoints[i - 1].t;
      return { sector: i + 1, abs: cp.t, delta: cp.t - prev };
    }),
  );
  const fastestPerSector: number[] = [];
  for (let s = 0; s < minCps; s++) {
    let fastIdx = 0;
    for (let r = 1; r < replays.length; r++) {
      if (sectorTimes[r][s].delta < sectorTimes[fastIdx][s].delta) fastIdx = r;
    }
    fastestPerSector.push(fastIdx);
  }

  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="eyebrow">Sector breakdown</div>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
          Fastest in sector highlighted purple (F1 convention)
        </div>
      </div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
        }}
      >
        <thead>
          <tr style={{ background: 'var(--bg-surface-2)' }}>
            <th
              style={{
                padding: '8px 14px',
                textAlign: 'left',
                color: 'var(--fg-3)',
                fontWeight: 500,
                fontSize: 10,
                letterSpacing: '0.08em',
              }}
            >
              SEC
            </th>
            {replays.map((r, i) => (
              <th
                key={r.meta.ghostUid}
                style={{
                  padding: '8px 14px',
                  textAlign: 'right',
                  color: colors[i],
                  fontWeight: 600,
                  fontSize: 11,
                }}
              >
                {r.meta.author}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: minCps }).map((_, s) => (
            <tr key={s} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: '8px 14px', color: 'var(--fg-2)', fontWeight: 600 }}>CP{s + 1}</td>
              {sectorTimes.map((replayTimes, ri) => {
                const isFast = fastestPerSector[s] === ri;
                const sec = replayTimes[s];
                return (
                  <td
                    key={ri}
                    style={{
                      padding: '8px 14px',
                      textAlign: 'right',
                      background: isFast ? 'rgba(181, 107, 255, 0.18)' : 'transparent',
                      color: isFast ? '#d6b8ff' : 'var(--fg-1)',
                      fontWeight: isFast ? 600 : 400,
                    }}
                  >
                    {formatTime(sec.delta * 1000)}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-surface-2)' }}>
            <td
              style={{
                padding: '10px 14px',
                color: 'var(--fg-3)',
                fontWeight: 600,
                fontSize: 10,
                letterSpacing: '0.08em',
              }}
            >
              FINAL
            </td>
            {replays.map((r, ri) => (
              <td
                key={ri}
                style={{
                  padding: '10px 14px',
                  textAlign: 'right',
                  color: r === fastest ? 'var(--ok)' : 'var(--fg-1)',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {r.meta.raceTimeStr}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DeltaTimeline({ replays, fastest, colors }: { replays: Replay[]; fastest: Replay; colors: string[] }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-8)',
        padding: '16px 18px',
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 16 }}>
        Cumulative delta vs. <span style={{ color: 'var(--ok)' }}>{fastest.meta.author}</span> (fastest)
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {replays.map((r, ri) => {
          if (r === fastest) return null;
          const n = Math.min(r.data.checkpoints.length, fastest.data.checkpoints.length);
          if (!n) return null;
          const cps: { sector: number; delta: number }[] = [];
          for (let i = 0; i < n; i++) {
            cps.push({ sector: i + 1, delta: r.data.checkpoints[i].t - fastest.data.checkpoints[i].t });
          }
          const maxAbs = Math.max(...cps.map((c) => Math.abs(c.delta)));
          return (
            <div key={r.meta.ghostUid}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 12, color: colors[ri], fontWeight: 600 }}>{r.meta.author}</span>
                <span
                  className="num"
                  style={{
                    fontSize: 13,
                    color: cps[cps.length - 1].delta > 0 ? 'var(--bad)' : 'var(--ok)',
                    fontWeight: 600,
                  }}
                >
                  {cps[cps.length - 1].delta > 0 ? '+' : ''}
                  {cps[cps.length - 1].delta.toFixed(3)}s
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'stretch', height: 38 }}>
                {cps.map((c) => {
                  const isLoss = c.delta > 0;
                  const pct = Math.min(1, Math.abs(c.delta) / (maxAbs + 0.01));
                  return (
                    <div
                      key={c.sector}
                      style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}
                    >
                      <div style={{ height: 18, display: 'flex', alignItems: 'flex-end' }}>
                        {isLoss && (
                          <div
                            style={{
                              width: '100%',
                              height: `${pct * 100}%`,
                              background: 'var(--bad)',
                              opacity: 0.7,
                              borderRadius: '2px 2px 0 0',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ height: 1, background: 'var(--fg-dim)' }} />
                      <div style={{ height: 18, display: 'flex', alignItems: 'flex-start' }}>
                        {!isLoss && (
                          <div
                            style={{
                              width: '100%',
                              height: `${pct * 100}%`,
                              background: 'var(--ok)',
                              opacity: 0.7,
                              borderRadius: '0 0 2px 2px',
                            }}
                          />
                        )}
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          top: -16,
                          left: 0,
                          right: 0,
                          textAlign: 'center',
                          fontSize: 9,
                          fontFamily: 'var(--font-mono)',
                          color: isLoss ? 'var(--bad)' : 'var(--ok)',
                        }}
                      >
                        {c.delta > 0 ? '+' : ''}
                        {c.delta.toFixed(2)}
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          bottom: -16,
                          left: 0,
                          right: 0,
                          textAlign: 'center',
                          fontSize: 9,
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--fg-muted)',
                        }}
                      >
                        CP{c.sector}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ height: 12 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
